import { StyleDeclarationValue, css } from 'aphrodite';
import { computed, makeObservable, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { NavLink } from 'react-router-dom';
import {
	ErrorMessagesViewModelKey,
	FullScreenModalViewModelKey,
	IFullscreenModalComponentProps,
	IUserSessionComponentProps,
	UserSessionViewModelKey,
} from '../../../models/AppState';
import { AccountAdminViewModel } from '../../../viewmodels/AdminViewModels';
import { NoteViewModel } from '../../../viewmodels/AppViewModels';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../../../web/components/HoverCard';
import { FlagIcon } from '../../../web/components/svgs/icons/FlagIcon';
import { AccountStatusIndicator } from '../AccountStatusIndicator';
import { AccountSummaryHoverNote } from '../AccountSummaryNotePopover';
import { styleSheet } from './styles';

interface IProps extends IFullscreenModalComponentProps, IUserSessionComponentProps {
	accountVM: AccountAdminViewModel;
	className?: string;
	isOwnedByMode?: boolean;
	styles?: StyleDeclarationValue[];
}

class _AccountRow extends React.Component<IProps> {
	private mNoteVM: NoteViewModel;

	constructor(props: IProps) {
		super(props);

		makeObservable<_AccountRow, 'mNoteVM' | 'elementDataProps'>(this, {
			mNoteVM: observable.ref,
			elementDataProps: computed,
		});

		this.mNoteVM = new NoteViewModel(props.userSession, {
			id: props.accountVM?.toJs()?.additionalInfo?.summaryNoteId ?? '',
		});
	}

	public render() {
		const { accountVM, isOwnedByMode, styles, className } = this.props;
		const account = accountVM.toJs();

		return (
			<div className={`${css(styleSheet.container, ...(styles || []))} ${className || ''}`} {...this.elementDataProps}>
				<div className={css(styleSheet.accountListStatusColor)}>
					<AccountStatusIndicator account={account} />
				</div>
				<div id={account.id} className={css(styleSheet.accountRowName)}>
					<HoverCard openDelay={300}>
						<HoverCardTrigger asChild onMouseEnter={this.loadNote}>
							<div className={css(styleSheet.accountName)}>
								<NavLink to={'/accounts/' + account.id} className={css(styleSheet.companyName)}>
									{account?.companyName || ''}
								</NavLink>
								{account?.additionalInfo?.accountFlagged && (
									<FlagIcon className={css(styleSheet.flagIcon)} isAccount={true} flagged={true} />
								)}
							</div>
						</HoverCardTrigger>
						<HoverCardContent>
							<AccountSummaryHoverNote account={accountVM} />
						</HoverCardContent>
					</HoverCard>
					<div className={css(styleSheet.emailDomain)}>
						{account.emailDomain === '@none.com' ? (
							<>
								<span>{account?.emailProviderConfigurations[0]?.domain}</span>
								<span className={css(styleSheet.andMore)}>
									{' and ' + (account?.emailProviderConfigurations?.length - 1) + ' more...'}
								</span>
							</>
						) : (
							account.emailDomain.replace('@', '')
						)}
					</div>
				</div>
				{isOwnedByMode ? (
					<>
						<div className={css(styleSheet.column)}>{account?.additionalInfo?.industry || ''}</div>
						<div className={css(styleSheet.column)}>{account?.additionalInfo?.stage || ''}</div>
						<div className={css(styleSheet.column)}>
							{account?.creationDate ? moment(account.creationDate).format('MM/DD/YYYY') : ''}
						</div>
						<div className={css(styleSheet.column)}>
							{account?.planDetails?.renewalDate ? moment(account.planDetails.renewalDate).format('MM/DD/YYYY') : ''}
						</div>
						<div className={css(styleSheet.column)}>
							{account?.additionalInfo?.lastCsmMeeting
								? moment(account.additionalInfo.lastCsmMeeting).format('MM/DD/YYYY')
								: ''}
						</div>
						<div className={css(styleSheet.columnLast)}>
							{account?.additionalInfo?.nextMeeting
								? moment(account.additionalInfo.nextMeeting).format('MM/DD/YYYY')
								: ''}
						</div>
					</>
				) : (
					<>
						<div className={css(styleSheet.column)}>{account?.additionalInfo?.industry || ''}</div>
						{account?.additionalInfo?.customerSuccessName ? (
							<div className={css(styleSheet.column)}>{account?.additionalInfo?.customerSuccessName}</div>
						) : (
							<div className={`${css(styleSheet.column)} ${css(styleSheet.unassigned)}`}>Unassigned</div>
						)}
						<div className={css(styleSheet.column)}>
							{account?.creationDate ? moment(account.creationDate).format('MM/DD/YYYY') : ''}
						</div>
						<div className={css(styleSheet.column)}>
							{account?.planDetails?.renewalDate ? moment(account.planDetails.renewalDate).format('MM/DD/YYYY') : ''}
						</div>
						<div className={css(styleSheet.column)}>
							{account?.additionalInfo?.lastCsmMeeting
								? moment(account.additionalInfo.lastCsmMeeting).format('MM/DD/YYYY')
								: ''}
						</div>
						<div className={css(styleSheet.columnLast)}>
							{account?.additionalInfo?.nextMeeting
								? moment(account.additionalInfo.nextMeeting).format('MM/DD/YYYY')
								: ''}
						</div>
					</>
				)}
			</div>
		);
	}

	private get elementDataProps() {
		if (!import.meta.env.PROD && this.props.accountVM) {
			return {
				['data-id']: this.props.accountVM.id || '',
			};
		}
		return {};
	}

	private loadNote = () => {
		if (this.mNoteVM.id && !this.mNoteVM.plainTextContent) {
			this.mNoteVM.load()?.catch(() => {
				/* swallow this error without notifying the user. Not much we can do.*/
			});
		}
	};
}

const ObservableAccountRow = observer(_AccountRow);
export const AccountRow = inject(
	ErrorMessagesViewModelKey,
	FullScreenModalViewModelKey,
	UserSessionViewModelKey
)(ObservableAccountRow);
